// Every CRUD write of the app, and its System log entry (P13). The ONLY file that calls
// `crudApi.create`, `.update` or `.remove` (`validate.sh` checks it): a write states its entry —
// what it changed, old → new, as a `ChangeSpec` — or the action whose entry covers it
// (`{ partOf }`, e.g. a line's pick ticket copies on Approve), or that it is the person's own setting
// (`{ own }`: a saved view — never in the people-facing System log; audit_log keeps it). After the save, its `change-entry`
// is created with the person (id and name) and the app's version; when is its `created_at`.
//
// OUTSIDE-PLATFORM: P13 apps cannot read audit_log, so the app keeps this copy.
// The entry is a second call after a save that already happened (OUTSIDE-PLATFORM: P4 no
// transaction): it is retried, and if it still fails the big red message says the save is in but
// not in the System log — never dropped in silence. The entry never undoes the save.

import { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CHANGE_ENTRIES, encodeChanges } from "@app/shared";
import type { CrudEntity } from "@app/shared";
import { crudApi, crudErrorText, useBuildInfo, useMe } from "@axny/platform-ui";
import { useMessage } from "./message";
import type { Log, LoggedWrites } from "./logged-write-core";
import { loggedWriter } from "./logged-write-core";

export type { Log, LoggedWrites } from "./logged-write-core";

/** Waits between tries of an entry: three tries in all. */
const RETRY_MS = [600, 2000];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useLoggedWrites(): LoggedWrites {
  const me = useMe();
  const build = useBuildInfo();
  const message = useMessage();
  const queryClient = useQueryClient();
  // Read at each write: the identity and the version arrive after the first render.
  const who = useRef({ id: "", name: "", version: "" });
  const user = me.data?.authenticated ? me.data.user : null;
  who.current = {
    id: user?.userId ?? "",
    name: user ? user.displayName || user.username : "",
    version: (build.data?.commit?.slice(0, 7) ?? build.data?.version ?? "").slice(0, 40),
  };

  const entry = async (entity: CrudEntity, recordId: string, log: Log) => {
    if ("partOf" in log || "own" in log) return;
    const input = {
      entity: entity.id,
      recordId: recordId.slice(0, 20),
      lineId: (log.lineId ?? (entity.id === "routing-line" ? recordId : "")).slice(0, 20),
      action: log.action,
      changes: encodeChanges(log.changes ?? []),
      note: (log.note ?? "").slice(0, 500),
      lineText: (log.lineText ?? "").slice(0, 200),
      personId: who.current.id.slice(0, 128) || "unknown",
      personName: who.current.name.slice(0, 128),
      appVersion: who.current.version,
      // The entry's own time (009). The list gives created_at's time too since D165; savedAt is still read.
      savedAt: new Date().toISOString(),
    };
    let last: unknown;
    for (let attempt = 0; attempt <= RETRY_MS.length; attempt++) {
      try {
        await crudApi.create(CHANGE_ENTRIES.id, input);
        // The System log and every line history show it now, not at their next minute.
        void queryClient.invalidateQueries({ queryKey: ["routing", "changes"] });
        return;
      } catch (e) {
        last = e;
        if (attempt < RETRY_MS.length) await sleep(RETRY_MS[attempt]!);
      }
    }
    // The save is in: say so, and that the System log misses it. Not awaited: the action goes on.
    void message.error(
      "Saved, but not written to the System log — tell Dijana",
      "Your change is saved and everyone sees it. Its line in the System log could not be written, so it can't be restored from there.",
      [`${entity.label} ${recordId}: ${log.action}`, crudErrorText(last)],
    );
  };

  // The save, then its entry — only once the save answered 2xx (`logged-write-core.ts`).
  return loggedWriter(
    {
      create: (entity, input) => crudApi.create(entity, input),
      update: (entity, id, rv, input) => crudApi.update(entity, id, rv, input),
      remove: (entity, id, rv) => crudApi.remove(entity, id, rv),
    },
    entry,
  );
}
